/**
 * Tone + appearance classes for `Notice`, the canonical inline messaging
 * primitive (errors, warnings, successes, informational callouts).
 *
 * Hue mapping is deliberately identical to `STATUS_VARIANT_CLASS` in
 * `@quovy/field-types` (`src/internal/status-variant-tokens.ts`) so a
 * `StatusBadge` and a `Notice` describing the same condition read as the same
 * colour: info → blue, success → green, warning → yellow, error → red,
 * neutral → muted. If you change a hue here, change it there too.
 *
 * Deliberate contrast with a badge: a badge is a small pill, so it can tint
 * its own text. A notice is a full-width surface, so only the *surface*, the
 * *border* and the *icon* carry tone. Title text stays `text-foreground` and
 * body text stays `text-foreground/80` — a wall of red body copy is
 * unreadable and reads as panic rather than as information. (`foreground/80`
 * rather than `muted-foreground`: the latter measures ~4.35:1 against these
 * tinted surfaces, just under WCAG AA.)
 */

import { cva } from "class-variance-authority";

export const NOTICE_TONES = ["info", "success", "warning", "error", "neutral"] as const;

export type NoticeTone = (typeof NOTICE_TONES)[number];

export const NOTICE_APPEARANCES = ["banner", "inline"] as const;

export type NoticeAppearance = (typeof NOTICE_APPEARANCES)[number];

/** Surface (background + border) per tone. Only applied by the `banner` appearance. */
export const NOTICE_SURFACE_CLASS: Record<NoticeTone, string> = {
  info: "border-blue-200 bg-blue-50 dark:border-blue-900/60 dark:bg-blue-950/30",
  success: "border-green-200 bg-green-50 dark:border-green-900/60 dark:bg-green-950/30",
  warning: "border-yellow-200 bg-yellow-50 dark:border-yellow-900/60 dark:bg-yellow-950/30",
  error: "border-red-200 bg-red-50 dark:border-red-900/60 dark:bg-red-950/30",
  neutral: "border-border bg-muted/40",
};

/** Icon colour per tone. Applied by every appearance — the icon is the tone signal. */
export const NOTICE_ICON_CLASS: Record<NoticeTone, string> = {
  info: "text-blue-600 dark:text-blue-400",
  success: "text-green-600 dark:text-green-400",
  warning: "text-yellow-700 dark:text-yellow-400",
  error: "text-red-600 dark:text-red-400",
  neutral: "text-muted-foreground",
};

/**
 * Body text colour for the `inline` appearance only. Inline notices have no
 * surface to carry the tone, and they are constrained to a single short
 * sentence (a field error, a row-level warning), so the text itself is tinted.
 * The `banner` appearance never does this — see the note at the top of the file.
 */
export const NOTICE_INLINE_TEXT_CLASS: Record<NoticeTone, string> = {
  info: "text-muted-foreground",
  success: "text-green-700 dark:text-green-400",
  warning: "text-yellow-700 dark:text-yellow-400",
  error: "text-destructive",
  neutral: "text-muted-foreground",
};

// `text-sm` is the default size, not a floor: `cn()` runs tailwind-merge, so a
// caller in a dense `text-xs` region passes `className="text-xs"` and wins.
export const noticeVariants = cva("text-sm", {
  variants: {
    appearance: {
      // A banner owns its row and stretches. An inline notice sits next to the
      // thing it describes, so it sizes to its sentence rather than stretching
      // a flex/grid parent — `max-w-full` still lets long copy wrap.
      banner: "w-full rounded-lg border px-4 py-3",
      inline: "w-fit max-w-full",
    },
    tone: {
      info: "",
      success: "",
      warning: "",
      error: "",
      neutral: "",
    },
  },
  compoundVariants: NOTICE_TONES.map((tone) => ({
    appearance: "banner" as const,
    tone,
    class: NOTICE_SURFACE_CLASS[tone],
  })),
  defaultVariants: {
    appearance: "banner",
    tone: "info",
  },
});

/**
 * Screen-reader urgency. Errors and warnings interrupt (`alert` →
 * `aria-live="assertive"`); everything else is announced politely (`status`).
 */
export function noticeRole(tone: NoticeTone): "alert" | "status" {
  return tone === "error" || tone === "warning" ? "alert" : "status";
}
